// Fixed-timestep game loop with an accumulator + render interpolation.
//
// Why fixed-timestep: walkit's loops call update straight off requestAnimationFrame's
// delta, so physics feel drifts with framerate (a 120Hz iPhone integrates gravity twice
// as often as a 60Hz one). Here update() always runs on a fixed dt, so feel is identical
// on every device; render() gets an `alpha` (0..1) to interpolate between the last two
// update states so motion still looks smooth at any refresh rate.

export interface LoopHandle {
  /** Start the loop (idempotent). */
  start(): void;
  /** Stop the loop; safe to start() again later. */
  stop(): void;
  /** True while running. */
  readonly running: boolean;
}

export interface LoopOptions {
  /** Fixed simulation step, in seconds. Default 1/60. */
  step?: number;
  /** Fixed-step simulation tick. `dt` is always `step`. */
  update: (dt: number) => void;
  /**
   * Draw. `alpha` is how far (0..1) we are between the previous and current
   * simulation state — use it to interpolate positions for smooth rendering.
   */
  render: (alpha: number) => void;
  /** Max sim steps per frame before we bail (spiral-of-death guard). Default 5. */
  maxSubSteps?: number;
  /** Pause the sim when the tab is hidden (default true). Prevents a huge catch-up burst on return. */
  pauseWhenHidden?: boolean;
}

export function createLoop(opts: LoopOptions): LoopHandle {
  const step = opts.step ?? 1 / 60;
  const maxSubSteps = opts.maxSubSteps ?? 5;
  const pauseWhenHidden = opts.pauseWhenHidden ?? true;

  let running = false;
  let rafId = 0;
  let last = 0;
  let acc = 0;

  const frame = (now: number) => {
    if (!running) return;
    rafId = requestAnimationFrame(frame);

    // now is in ms; convert to seconds. Clamp the very first frame's delta.
    let frameTime = (now - last) / 1000;
    last = now;
    if (frameTime > 0.25) frameTime = 0.25; // long stall (backgrounded, breakpoint) — don't spiral

    acc += frameTime;
    let steps = 0;
    while (acc >= step && steps < maxSubSteps) {
      opts.update(step);
      acc -= step;
      steps++;
    }
    if (steps === maxSubSteps) acc = 0; // we're behind; drop the backlog rather than accumulate debt

    opts.render(acc / step);
  };

  const onVisibility = () => {
    if (!pauseWhenHidden) return;
    if (document.visibilityState === 'visible' && running) {
      last = performance.now();
      acc = 0;
    }
  };

  return {
    get running() {
      return running;
    },
    start() {
      if (running) return;
      running = true;
      last = performance.now();
      acc = 0;
      document.addEventListener('visibilitychange', onVisibility);
      rafId = requestAnimationFrame(frame);
    },
    stop() {
      running = false;
      cancelAnimationFrame(rafId);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
